import { supabase } from "../supabase/SupabaseClient";

/* ¿De quién es este papel? Sugerencias para las actas del pañol sin DNI.
 *
 * Nico, 06/10, mirando «35 papeles sin una persona del padrón»: ¿cómo se
 * corrige eso? Abriendo cada uno y buscando a mano a alguien que el
 * reconocimiento leyó como «Acoson Cardel. M. / Acosts».
 *
 * Casi todas las actas tienen el interno, y la base sabe quién manejó cada
 * interno (`actas_sugerencias()`, migración 20261007010000). Acá se compara
 * ese chofer con lo que dice el papel y se arma la sugerencia: primero los
 * que además se llaman parecido. Nunca se asigna solo: el Hub muestra
 * «¿Es él?» y la persona elige.
 */

/* Las palabras de un nombre, sin tildes ni «comillas» ni basura del OCR. */
export function palabras(texto) {
  return String(texto || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .split(/[^a-zñ]+/)
    .filter((p) => p.length >= 3 && p !== "sin" && p !== "nombre");
}

/* Distancia de edición, cortada: sólo importa si es 0, 1 o 2. */
function cerca(a, b) {
  if (a === b) return true;
  if (Math.abs(a.length - b.length) > 2) return false;
  if (Math.min(a.length, b.length) < 4) return false;
  let prev = Array.from({ length: b.length + 1 }, (_, j) => j);
  for (let i = 1; i <= a.length; i += 1) {
    const fila = [i];
    for (let j = 1; j <= b.length; j += 1) {
      fila[j] = Math.min(prev[j] + 1, fila[j - 1] + 1,
                         prev[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
    }
    prev = fila;
  }
  return prev[b.length] <= 2;
}

/* Cuántas palabras del papel aparecen (o casi) en el nombre del padrón.
   «Acosts» ≈ «acosta», «BROZZI» ≈ «briozzi», «Exelhoo» no: cuenta 0. */
export function parecido(textoPapel, persona) {
  const delPapel = palabras(textoPapel);
  const delPadron = palabras(`${persona?.apellido || ""} ${persona?.nombres || ""}`);
  return delPapel.filter((p) => delPadron.some((q) => cerca(p, q)
    || (p.length >= 5 && q.startsWith(p)) || (q.length >= 5 && p.startsWith(q)))).length;
}

/* entrega_id → [{ persona, interno, puntos }], de más a menos parecido. */
export function ordenarSugerencias(filas, actas) {
  const nombrePorActa = new Map((actas || []).map((a) => [String(a.id), a.persona_nombre]));
  const porActa = new Map();
  for (const f of filas || []) {
    const id = String(f.entrega_id);
    const persona = { dni: f.dni, apellido: f.apellido, nombres: f.nombres,
                      legajo: f.legajo, puesto: f.puesto };
    const lista = porActa.get(id) || [];
    if (!lista.some((s) => String(s.persona.dni) === String(f.dni))) {
      lista.push({ persona, interno: f.interno,
                   puntos: parecido(nombrePorActa.get(id), persona) });
    }
    porActa.set(id, lista);
  }
  for (const lista of porActa.values()) lista.sort((a, b) => b.puntos - a.puntos);
  return porActa;
}

export async function sugerencias(actas) {
  const { data, error } = await supabase.rpc("actas_sugerencias");
  if (error) {
    // Sin la función (migración sin correr) o sin permiso: se busca a mano, como antes.
    console.warn("[herramientas] sin sugerencias:", error.message);
    return new Map();
  }
  return ordenarSugerencias(data, actas);
}
